// Persistent user preferences (Zustand + localStorage). NEW feature state — kept
// separate from mapStore (transient tool-bridge) and from App's React state. Any
// feature that needs a durable per-user setting adds a field here.
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const DEFAULT_PREFS = {
  coordFormat: 'DD',        // 'DD' (decimal degrees) | 'DMS' (deg/min/sec)
  coordsCollapsed: false,   // fold the on-map coordinates readout
  defaultProjection: 'mercator', // 'mercator' | 'globe' — applied on load
  defaultLayer: 'countries',     // layer key pre-selected on load
  borderWidth: 1,
  borderColor: '#333333',
  borderOpacity: 1,              // 0..1
  borderStyle: 'solid',          // 'solid' | 'dashed' | 'dotted' | 'mixed'
  haloEnabled: true,             // country halo (glow) on selected / own country
  haloColor: '#D5B654',          // country halo glow colour
  bubbleColor: '#4FD0E0',        // bubble-map circle colour
  bubbleSize: 1,                 // bubble-map size multiplier (0.5..2)
  palette: 'cividis',            // readable under common colour-vision deficiencies
  classification: 'quantile',    // 'quantile' | 'percentile' | 'log' | 'linear'
  nClasses: 7,                   // choropleth class count (3..9)
  customRamp: null,              // custom palette (array of hex) when palette==='custom'
  theme: 'dark',                 // 'dark' | 'light'
  hiddenSections: [],            // actions-menu section ids the user has hidden
}

export const usePrefsStore = create(
  persist(
    (set) => ({
      ...DEFAULT_PREFS,
      setPref: (key, value) => set({ [key]: value }),
      setPrefs: (partial) => set(partial),
      resetPrefs: () => set({ ...DEFAULT_PREFS }),
    }),
    {
      name: 'pr-onlinemap-prefs',
      // Only persist the known keys (drop the action fns).
      partialize: (s) => Object.fromEntries(Object.keys(DEFAULT_PREFS).map((k) => [k, s[k]])),
      // v1: 'linear' was the old default and rendered the density and PIB/hab
      // layers as one flat near-white sheet (both metrics are far too skewed
      // for equal intervals). Changing DEFAULT_PREFS alone would only fix
      // first-time visitors, so the stored value is migrated once. A user who
      // deliberately picks 'linear' afterwards keeps it — the migration runs
      // only when the persisted version is older than 1.
      version: 2,
      migrate: (state, version) => {
        let next = state
        if (version < 1 && next?.classification === 'linear') {
          next = { ...next, classification: DEFAULT_PREFS.classification }
        }
        if (version < 2 && (!next?.palette || next.palette === 'default')) {
          next = { ...next, palette: DEFAULT_PREFS.palette }
        }
        return next
      },
    }
  )
)
